import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react'
import { getCurrentUser, logout as apiLogout, type CurrentUser } from '../api/auth'
import { broadcastSessionChange, watchForOrgChange } from '../lib/crossTabAuth'

interface AuthState {
  user: CurrentUser | null
  loading: boolean
  /** true when SAML is configured AND user is authenticated */
  authenticated: boolean
  /** true when SSO/SAML is enabled on the server */
  ssoEnabled: boolean
  /** Call to log out: clears server session then resets auth state. */
  signOut: () => Promise<void>
  /** Re-fetch the current user (e.g. after a profile update). */
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  authenticated: false,
  ssoEnabled: false,
  signOut: async () => {},
  refresh: async () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [loading, setLoading] = useState(true)

  // What this tab loaded as: undefined until /auth/me first settles, then
  // the org id, or null when not signed in. A ref so the watcher reads the
  // latest value without re-subscribing.
  const loadedOrgRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (!loading) loadedOrgRef.current = user?.org?.id ?? null
  }, [loading, user])

  // REG-273: another tab signing in or out changes this browser's one
  // session cookie; reload rather than keep showing the old org's data.
  useEffect(
    () =>
      watchForOrgChange(
        () => loadedOrgRef.current,
        async () => (await getCurrentUser()).org?.id ?? null,
        () => window.location.reload()
      ),
    []
  )

  useEffect(() => {
    getCurrentUser()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  const refresh = useCallback(async () => {
    try {
      const u = await getCurrentUser()
      setUser(u)
    } catch {
      // ignore
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      await apiLogout()
    } catch {
      // session may already be gone — proceed regardless
    }
    setUser(null)
    broadcastSessionChange()
  }, [])

  const value: AuthState = {
    user,
    loading,
    authenticated: user?.authenticated === true,
    ssoEnabled: user?.ssoEnabled === true,
    signOut,
    refresh,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  return useContext(AuthContext)
}
